/**
 * Purchase orders — a BUYER's order TO XOP Networks (2026-09-17).
 *
 * The direction is the whole point, and the opposite of every other money
 * document in this portal: an invoice and a quote come FROM XOP Networks, a
 * purchase order goes TO it. A reseller makes one in two ways — from a quote it
 * has just accepted (crm-api copies the lines and renders the standard PO PDF),
 * or by uploading its own PO document. Staff can also upload one that arrived
 * by email; the reseller then reads it here.
 *
 * The rows ride the BFF's `/api/crm/v1/*` PASSTHROUGH, so what reaches the
 * browser is crm-api's own snake_case row. This file is the one place that
 * turns it into the portal's camelCase shape (`toPurchaseOrder`) — pure, so the
 * mapping is unit-tested without a browser or a server. The contract is
 * crm-api's `purchase_orders` serializer (v1, 2026-09-17).
 */

export const PURCHASE_ORDER_STATUSES = ["received", "accepted", "cancelled"] as const;
export type PurchaseOrderStatus = (typeof PURCHASE_ORDER_STATUSES)[number];

export const PURCHASE_ORDER_STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
  received: "Received",
  accepted: "Accepted",
  cancelled: "Cancelled",
};

/** `quote` = made from an accepted XOP quote; `reseller` = a reseller uploaded
 * its own PO; `upload` = XOP staff filed one that arrived by email. */
export const PURCHASE_ORDER_SOURCES = ["quote", "reseller", "upload"] as const;
export type PurchaseOrderSource = (typeof PURCHASE_ORDER_SOURCES)[number];

/** What the list's Source column says. A reseller does not care WHO uploaded
 * the file, only that it was a file rather than a quote. */
export const PURCHASE_ORDER_SOURCE_LABELS: Record<PurchaseOrderSource, string> = {
  quote: "From quote",
  reseller: "Uploaded",
  upload: "Uploaded",
};

export interface PurchaseOrderLine {
  partNumber: string;
  description: string;
  unit: string | null;
  quantity: number;
  /** Minor units. Null when crm-api stripped it (a staff token without
   * financials) — never for a reseller, but the type says what the wire can. */
  unitPriceCents: number | null;
  lineTotalCents: number | null;
}

/** The buyer or ship-to block, as crm-api printed it on the PO. */
export interface PurchaseOrderParty {
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  email: string | null;
}

export interface PurchaseOrderDocument {
  filename: string;
  contentType: string | null;
  byteSize: number | null;
}

/** `{ id, number }` — how a quote points at its purchase order and back. */
export interface DocumentRef {
  id: number;
  number: string | null;
}

export interface PurchaseOrder {
  id: number;
  /** XOP's record number, `PO-2026-0007`. Always allocated by crm-api. */
  number: string | null;
  /** The BUYER's own PO number; null until someone enters it. */
  poNumber: string | null;
  source: PurchaseOrderSource;
  status: PurchaseOrderStatus;
  orderDate: string | null;
  currency: string | null;
  lines: PurchaseOrderLine[];
  subtotalCents: number | null;
  totalCents: number | null;
  customer: { id: number; companyName: string | null } | null;
  reseller: { id: number; name: string | null; code: string | null } | null;
  resellerQuote: DocumentRef | null;
  buyer: PurchaseOrderParty | null;
  shipTo: PurchaseOrderParty | null;
  note: string | null;
  submittedByEmail: string | null;
  hasDocument: boolean;
  document: PurchaseOrderDocument | null;
  /** crm-api can render the standard PO PDF (it has lines). */
  hasPdf: boolean;
  /** crm-api's verdict for THIS actor. Never re-derived in the browser — for a
   * reseller it is "status is received", but that rule is the server's. */
  editable: boolean;
  /**
   * crm-api's verdict on CANCELLING, for this actor (2026-09-17). The second
   * verdict rather than a reading of `editable`, because the two answer
   * different questions and will drift: a reseller may correct its PO number
   * on an order it may also withdraw today, and either half could change
   * without the other. The browser renders the verdict; it never compares
   * `status` to decide whether to offer the button.
   */
  cancellable: boolean;
  /** When the order was cancelled, ISO-8601, or null while it stands. */
  cancelledAt: string | null;
  /** WHO cancelled it, as a human label crm-api wrote ("Acme Distribution ·
   * buyer@acme.test", or a staff name). Never an id to resolve here. */
  cancelledBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface PurchaseOrderPagination {
  page: number;
  perPage: number;
  totalPages: number;
  totalCount: number;
}

export type PurchaseOrderCounts = Record<PurchaseOrderStatus, number>;

export interface PurchaseOrderList {
  purchaseOrders: PurchaseOrder[];
  pagination: PurchaseOrderPagination;
  counts: PurchaseOrderCounts;
}

/* ── mapping ─────────────────────────────────────────────────────────── */

function rec(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function str(value: unknown): string | null {
  if (typeof value === "string") return value.trim() === "" ? null : value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function num(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Integer minor units, or null — never NaN, and never a made-up 0. */
function cents(value: unknown): number | null {
  const parsed = num(value);
  return parsed === null ? null : Math.round(parsed);
}

function positiveId(value: unknown): number | null {
  const parsed = num(value);
  return parsed !== null && Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function toLine(raw: unknown): PurchaseOrderLine {
  const l = rec(raw);
  return {
    partNumber: str(l["part_number"]) ?? "",
    description: str(l["description"]) ?? "",
    unit: str(l["unit"]),
    quantity: Math.max(0, Math.round(num(l["quantity"]) ?? 0)),
    unitPriceCents: cents(l["unit_price_cents"]),
    lineTotalCents: cents(l["line_total_cents"]),
  };
}

function toParty(raw: unknown): PurchaseOrderParty | null {
  if (raw === null || typeof raw !== "object") return null;
  const p = rec(raw);
  return {
    name: str(p["name"]),
    address: str(p["address"]),
    city: str(p["city"]),
    state: str(p["state"]),
    postalCode: str(p["postal_code"]),
    country: str(p["country"]),
    email: str(p["email"]),
  };
}

/** `{ id, number }` or null — for `reseller_quote` and `purchase_order`. */
export function toDocumentRef(raw: unknown): DocumentRef | null {
  const r = rec(raw);
  const id = positiveId(r["id"]);
  return id === null ? null : { id, number: str(r["number"]) };
}

function isStatus(v: unknown): v is PurchaseOrderStatus {
  return typeof v === "string" && (PURCHASE_ORDER_STATUSES as readonly string[]).includes(v);
}

function isSource(v: unknown): v is PurchaseOrderSource {
  return typeof v === "string" && (PURCHASE_ORDER_SOURCES as readonly string[]).includes(v);
}

/**
 * One crm-api row as the portal's purchase order, or null when it has no
 * usable id (a row that cannot be opened is not a row worth listing).
 *
 * AN UNKNOWN STATUS READS AS `received` and an unknown `editable` as FALSE. The
 * first is the neutral record value; the second is the safe one — "we do not
 * recognise this" must never offer an edit the server would refuse. The same
 * goes for `cancellable`: a row from a crm-api that predates the field offers
 * no Cancel button rather than one that would 404.
 */
export function toPurchaseOrder(raw: unknown): PurchaseOrder | null {
  const row = rec(raw);
  const id = positiveId(row["id"]);
  if (id === null) return null;

  const customerRaw = rec(row["customer"]);
  const customerId = positiveId(customerRaw["id"]) ?? positiveId(row["customer_id"]);
  const resellerRaw = rec(row["reseller"]);
  const resellerId = positiveId(resellerRaw["id"]) ?? positiveId(row["reseller_id"]);
  const documentRaw = row["document"];
  const doc = rec(documentRaw);
  const lines = row["lines"];

  return {
    id,
    number: str(row["number"]),
    poNumber: str(row["po_number"]),
    source: isSource(row["source"]) ? row["source"] : "upload",
    status: isStatus(row["status"]) ? row["status"] : "received",
    orderDate: str(row["order_date"]),
    currency: str(row["currency"]),
    lines: Array.isArray(lines) ? lines.map(toLine) : [],
    subtotalCents: cents(row["subtotal_cents"]),
    totalCents: cents(row["total_cents"]),
    customer:
      customerId === null ? null : { id: customerId, companyName: str(customerRaw["company_name"]) },
    reseller:
      resellerId === null
        ? null
        : { id: resellerId, name: str(resellerRaw["name"]), code: str(resellerRaw["code"]) },
    resellerQuote: toDocumentRef(row["reseller_quote"]),
    buyer: toParty(row["buyer"]),
    shipTo: toParty(row["ship_to"]),
    note: str(row["note"]),
    submittedByEmail: str(row["submitted_by_email"]),
    hasDocument: row["has_document"] === true,
    document:
      documentRaw !== null && typeof documentRaw === "object" && str(doc["filename"]) !== null
        ? {
            filename: str(doc["filename"]) as string,
            contentType: str(doc["content_type"]),
            byteSize: num(doc["byte_size"]),
          }
        : null,
    hasPdf: row["has_pdf"] === true,
    editable: row["editable"] === true,
    cancellable: row["cancellable"] === true,
    cancelledAt: str(row["cancelled_at"]),
    cancelledBy: str(row["cancelled_by"]),
    createdAt: str(row["created_at"]),
    updatedAt: str(row["updated_at"]),
  };
}

/** `GET /purchase_orders`'s envelope, mapped. Missing counts read as 0 — they
 * label a filter, and a filter with no rows behind it genuinely has none. */
export function toPurchaseOrderList(raw: unknown): PurchaseOrderList {
  const body = rec(raw);
  const rows = Array.isArray(body["purchase_orders"]) ? (body["purchase_orders"] as unknown[]) : [];
  const purchaseOrders = rows
    .map(toPurchaseOrder)
    .filter((po): po is PurchaseOrder => po !== null);
  const p = rec(rec(body["meta"])["pagination"]);
  const c = rec(body["counts"]);
  return {
    purchaseOrders,
    pagination: {
      page: num(p["current_page"]) ?? 1,
      perPage: num(p["per_page"]) ?? 25,
      totalPages: num(p["total_pages"]) ?? 1,
      totalCount: num(p["total_count"]) ?? purchaseOrders.length,
    },
    counts: {
      received: num(c["received"]) ?? 0,
      accepted: num(c["accepted"]) ?? 0,
      cancelled: num(c["cancelled"]) ?? 0,
    },
  };
}

/* ── who cancelled it ────────────────────────────────────────────────── */

/**
 * Did this order's own BUYER cancel it, rather than XOP Networks?
 *
 * Only a question of WORDING. A reseller reading its own cancelled order
 * should be told "You cancelled this on …" and not "XOP Networks has
 * cancelled this purchase order" — the two sentences mean opposite things
 * about who changed their mind.
 *
 * crm-api writes `cancelled_by` as a human label, and for a reseller actor
 * that label names the company and the person ("Acme Distribution ·
 * buyer@acme.test"); for staff it names the XOP user. So the buyer's side is
 * recognised by the company's own name or the buyer's own address appearing
 * in it — both of which this row already carries.
 *
 * NEITHER MATCHING READS AS XOP, deliberately: an unrecognised label must
 * never be presented to a reseller as its own act, and the XOP wording names
 * the company rather than repeating a staff person's identity into the
 * portal.
 */
export function cancelledByBuyer(
  po: Pick<PurchaseOrder, "cancelledBy" | "reseller" | "buyer" | "submittedByEmail">,
): boolean {
  const label = po.cancelledBy?.toLowerCase();
  if (!label) return false;
  const mine = [po.reseller?.name, po.buyer?.name, po.buyer?.email, po.submittedByEmail]
    .filter((v): v is string => typeof v === "string" && v.trim() !== "")
    .map((v) => v.toLowerCase());
  return mine.some((v) => label.includes(v));
}

/* ── the upload rule ─────────────────────────────────────────────────── */

/** crm-api's own ceiling on a PO document. */
export const PURCHASE_ORDER_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

/** What crm-api accepts: PDF, an image, Word or Excel. The `accept` attribute
 * of the file input is built from the same list. */
export const PURCHASE_ORDER_DOCUMENT_EXTENSIONS = [
  ".pdf", ".png", ".jpg", ".jpeg", ".gif", ".webp", ".heic", ".tif", ".tiff",
  ".doc", ".docx", ".xls", ".xlsx",
] as const;

const DOCUMENT_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

/**
 * Why this file cannot be a PO document, or null when it can.
 *
 * Checked in the browser so a reseller hears "too big" before waiting for a
 * 10 MB upload to be refused. crm-api checks again — this is a courtesy.
 * The TYPE passes on either the declared MIME type or the extension, because
 * browsers leave `type` empty for some Office files on some systems.
 */
export function purchaseOrderDocumentProblem(file: {
  name: string;
  type: string;
  size: number;
}): string | null {
  const lower = file.name.toLowerCase();
  const typeOk =
    DOCUMENT_TYPES.has(file.type) ||
    file.type.startsWith("image/") ||
    PURCHASE_ORDER_DOCUMENT_EXTENSIONS.some((ext) => lower.endsWith(ext));
  if (!typeOk) return "That file type is not accepted. Attach a PDF, an image, a Word or an Excel file.";
  if (file.size <= 0) return "That file is empty.";
  if (file.size > PURCHASE_ORDER_DOCUMENT_MAX_BYTES) return "That file is larger than 10 MB.";
  return null;
}

/**
 * A typed amount ("1,250.50", "$99") as integer cents; `null` for an empty
 * field; `undefined` for something that is not an amount.
 *
 * Three answers because "left blank" and "typed nonsense" are different: the
 * first is allowed (the total is optional), the second is refused with a
 * sentence.
 */
export function parseAmountToCents(input: string): number | null | undefined {
  const trimmed = input.trim().replace(/^\$/, "").replace(/,/g, "").trim();
  if (trimmed === "") return null;
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return undefined;
  return Math.round(Number(trimmed) * 100);
}
